import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { getRunner } from "./game/runner";
import type {
  ClubOption,
  ClubView,
  ErroDaSessao,
  FraseDaSessao,
  LineupInput,
  MarketFilters,
  OrdemDoMercado,
  MarketPlayerRow,
  LiveView,
  MatchSummary,
  PlayerDetail,
  SaveMeta,
  TacticInput,
} from "./game/types";
import type { ClubKit, Datapack, EntradaDeClubeCriado } from "@legacy-manager/engine";
import { ClubHub, type HubActions } from "./screens/ClubHub";
import { Admin } from "./screens/Admin";
import { Conta } from "./screens/Conta";
import { HubOnline } from "./screens/HubOnline";
import { MercadoOnline } from "./screens/MercadoOnline";
import { modoInicial, modosDaPlataforma, ehAplicativo, type Modo } from "./game/modo";
import {
  apagarCarreira,
  baixarCarreira,
  carreiraDaConta,
  enviarCarreira,
  exportarCarreira,
  identificadorLocal,
  prepararCarreira,
  type Carreira,
} from "./online/carreira";
import { readSave } from "./game/save-store";
import { SincroniaDePush } from "./online/push";
import { ProvedorDeSessao, useSessao } from "./online/sessao";
import { SeasonEnd } from "./screens/SeasonEnd";
import { contextoDaPartida } from "./erros";
import { LiveMatch } from "./screens/LiveMatch";
import { Home } from "./screens/Home";
import { MatchPanel } from "./screens/MatchPanel";
import { CriarClube } from "./screens/CriarClube";
import { NewGame } from "./screens/NewGame";
import { Opcoes } from "./screens/Opcoes";
import type { DadosDaFicha } from "./screens/PlayerSheet";
import { AvisoDeVersao } from "./ui/AvisoDeVersao";
import { useAvisos } from "./ui/avisos";
import { preferenciaLigada } from "./preferencias";
import { useTema } from "./ui/tema";
import { SeletorDeIdioma, SincroniaDeIdioma } from "./ui/SeletorDeIdioma";
import { problemaDeDatapack, textoDaSessao, useT } from "./i18n";
import { recusaDoErro } from "./game/types";

type Screen =
  | "inicio"
  | "gerando"
  | "escolha"
  | "criarClube"
  | "clube"
  | "editor"
  | "conta"
  | "opcoes"
  /*
   * Ligas, loja e ranqueado saíram daqui.
   *
   * Elas viraram seção do hub e rodam dentro do jogo. "liga" continua, no
   * singular, porque jogar uma liga online é outro mundo e outro clube: ali a
   * tela troca de verdade.
   */
  | "liga"
  | "mercado";

/**
 * A frase de um erro de decisão.
 *
 * Nada que vem do worker é frase: o motor e a sessão solo devolvem chave e
 * valores, e a frase é escrita aqui, na thread da tela, que é a única que sabe
 * qual idioma a pessoa escolheu.
 */
function textoDoErro(erro: ErroDaSessao): string {
  return textoDaSessao(erro.key, erro.params);
}

/**
 * O editor entra sob demanda.
 *
 * É a única tela da interface que precisa do motor do lado de cá: a validação
 * do datapack e a conta de força moram nele. Carregar isso junto do resto
 * engordaria o pacote inicial de quem só quer jogar, e o guia de frontend pede
 * o contrário. Quem abre o editor espera meio segundo a mais, uma vez.
 */
const DatabaseEditor = lazy(async () => ({
  default: (await import("./screens/DatabaseEditor")).DatabaseEditor,
}));

/**
 * Uma camisa lisa com as duas cores do clube.
 *
 * Clube gerado não tem uniforme desenhado: só quem criou o próprio no criador
 * tem. Mas todo clube tem duas cores, e camisa lisa com elas é o que a tabela
 * já usa no escudo. Sem isto, a ficha do elenco mostraria camisa para quem
 * criou o clube e nada para os outros trezentos e vinte.
 */
function uniformeDasCores(cores: [string, string]): ClubKit {
  return { pattern: "solid", primary: cores[0], secondary: cores[1], badge: null };
}

/**
 * A árvore inteira do jogo vive dentro da sessão.
 *
 * Antes o provedor de sessão era aberto no meio do `return` do `App`, o que
 * deixava o próprio `App` fora dele: quem estava do lado de fora não conseguia
 * chamar `useSessao()`. Isso servia enquanto a conta era um extra; agora a
 * carreira **é** a conta, e a tela inicial precisa saber quem entrou antes de
 * decidir o que mostrar.
 */
/**
 * O painel administrativo abre por endereço, e não por botão.
 *
 * O jogo não tem roteador, e não vale a pena ter um por causa de uma tela: as
 * outras todas são estado. `/admin` funciona porque o servidor devolve o
 * `index.html` para qualquer caminho, e aqui o caminho é lido uma vez, na
 * montagem.
 *
 * Quem não for administrador chega numa tela que diz isso e volta. A guarda de
 * verdade é do servidor, que confere o papel contra o banco em toda chamada; o
 * que decidimos aqui é só o que desenhar.
 */
function ehCaminhoDoPainel(): boolean {
  return typeof window !== "undefined" && window.location.pathname.replace(/\/+$/, "") === "/admin";
}

export function App() {
  const [noPainel, definirNoPainel] = useState(ehCaminhoDoPainel);

  const sairDoPainel = useCallback(() => {
    window.history.pushState(null, "", "/");
    definirNoPainel(false);
  }, []);

  // O botão de voltar do navegador precisa funcionar como em qualquer site.
  useEffect(() => {
    const aoAndar = (): void => definirNoPainel(ehCaminhoDoPainel());
    window.addEventListener("popstate", aoAndar);
    return () => window.removeEventListener("popstate", aoAndar);
  }, []);

  return (
    <ProvedorDeSessao>
      {noPainel ? <Admin aoVoltar={sairDoPainel} /> : <Jogo />}

      {/*
        O aviso de versão vive aqui, e não dentro do `Jogo`.

        Lá dentro ele não existia em `/admin`, e `/admin` é justamente onde
        alguém abre o navegador direto num endereço, sem passar pelo jogo. Um
        cliente preso numa versão velha só se solta aceitando este aviso: se ele
        não é desenhado, não há como sair, e o service worker continua servindo
        o pacote antigo para sempre.

        Isso deixou de ser hipótese quando a API saiu do IP e foi para o
        domínio: o pacote velho passou a chamar `http://82.38.28.29` de dentro
        de uma página `https`, o navegador bloqueou por conteúdo misto, e quem
        estava em `/admin` não tinha nem o aviso para clicar.
      */}
      <AvisoDeVersao />
    </ProvedorDeSessao>
  );
}

function Jogo() {
  const t = useT();
  const [screen, setScreen] = useState<Screen>("inicio");
  /*
   * O país e o nome do treinador, guardados ao sair da escolha de clube.
   *
   * O criador precisa dos dois e não tem como perguntar de novo: o nome já foi
   * digitado na tela anterior, e repetir o campo seria pedir duas vezes a mesma
   * coisa.
   */
  const [criacao, setCriacao] = useState<{ countryId: string; managerName: string } | null>(null);
  // A escolha da tela de opções. Mora aqui porque é aqui que o gatilho do
  // envio é montado, e desligar precisa valer já, sem reabrir o jogo.
  const [options, setOptions] = useState<ClubOption[]>([]);
  const [generationMs, setGenerationMs] = useState(0);
  const [view, setView] = useState<ClubView | null>(null);
  const [live, setLive] = useState<LiveView | null>(null);
  const [match, setMatch] = useState<MatchSummary | null>(null);
  const [sheet, setSheet] = useState<PlayerDetail | null>(null);
  const [market, setMarket] = useState<{
    results: MarketPlayerRow[];
    total: number;
    pagina: number;
    paginas: number;
  }>({
    results: [],
    total: 0,
    pagina: 1,
    paginas: 1,
  });
  const [saves, setSaves] = useState<SaveMeta[]>([]);
  /**
   * Banco que está aberto no editor.
   *
   * Fica aqui só para sobreviver à ida e volta da tela: se o motor recusar o
   * arquivo na hora de começar a carreira, a pessoa volta ao editor com a
   * edição inteira no lugar em vez de perder a tarde de trabalho.
   */
  const [packDoEditor, setPackDoEditor] = useState<Datapack | null>(null);
  /**
   * Liga online aberta.
   *
   * Guarda só o identificador, de propósito: o hub online lê o resto do
   * servidor a cada abertura. Guardar a liga inteira aqui criaria uma segunda
   * cópia do estado, que envelhece sozinha e diverge da que o servidor tem.
   */
  const [ligaOnline, setLigaOnline] = useState<string | null>(null);
  /**
   * Recado do servidor que não vale interromper ninguém.
   *
   * Envio adiado, servidor fora: nada disso impede de jogar. A rodada já
   * aconteceu no aparelho e o próximo envio leva o mundo inteiro, porque ele é
   * um só. Tratar como erro cortaria a partida de quem está no meio de uma
   * temporada por um problema que não é dela.
   */
  /*
   * O aviso da nuvem é um recado como qualquer outro, e vai para a mesma pilha.
   *
   * Ele tinha componente próprio, num canto diferente do resto: dois sistemas
   * de aviso na mesma tela, cada um com a própria aparência e o próprio lugar.
   * Ele é `recado` e não `erro` porque nada disso impede de jogar: a rodada já
   * aconteceu no aparelho e o próximo envio leva tudo junto.
   */
  const { avisar } = useAvisos();

  /*
   * O tema do brasão precisa saber de qual clube tirar cor.
   *
   * Quem tem a visão do clube na mão é aqui, e não o provedor de tema: ele é
   * de interface e não sabe o que é carreira. O escudo desenhado tem
   * preferência sobre as duas cores sorteadas, porque é o que a pessoa fez.
   *
   * Sem carreira aberta manda `null`, e aí o tema do brasão fica indisponível
   * na tela de opções em vez de cair num cinza que ninguém escolheu.
   */
  const { definirCoresDoClube } = useTema();
  const clube = view?.club;
  useEffect(() => {
    if (!clube) {
      definirCoresDoClube(null);
      return;
    }
    definirCoresDoClube(
      clube.crest
        ? { primary: clube.crest.primary, secondary: clube.crest.secondary, detail: clube.crest.detail }
        : { primary: clube.colors[0], secondary: clube.colors[1], detail: clube.colors[1] },
    );
  }, [clube, definirCoresDoClube]);
  const setAvisoDaNuvem = useCallback(
    (texto: string | null) => {
      if (texto) avisar(texto, "recado");
    },
    [avisar],
  );
  const [busy, setBusy] = useState(false);

  /*
   * Enquanto a rodada roda, o ponteiro vira ampulheta.
   *
   * O aviso fica no `body` e não numa classe de componente porque o ponteiro é
   * do documento inteiro: trocar só dentro do painel deixaria metade da tela
   * dizendo "clique" enquanto nada responde a clique. Quem desenha isso é
   * `styles/arte.css`, e só onde existe ponteiro de verdade.
   */
  useEffect(() => {
    document.body.dataset["ocupado"] = busy ? "sim" : "nao";
  }, [busy]);

  /*
   * A carreira da conta.
   *
   * No navegador o jogo é só online, então `modo` já nasce decidido. No
   * aplicativo instalado ele começa nulo e a tela inicial pergunta entre
   * 1 Jogador e Online Multiplayer.
   */
  const sessao = useSessao();
  const [modo, setModo] = useState<Modo | null>(() => modoInicial());
  const [carreira, setCarreira] = useState<Carreira | null>(null);
  const [carregandoCarreira, setCarregandoCarreira] = useState(false);
  /**
   * De qual conta a resposta da carreira já chegou.
   *
   * "Não está carregando" não quer dizer "já perguntou": antes da primeira
   * pergunta as duas coisas são falsas do mesmo jeito, e quem olhasse só a
   * bandeira de carregamento leria `carreira: null` como "esta conta não tem
   * carreira" no exato instante em que a sessão apareceu e a pergunta ainda
   * nem tinha saído.
   */
  const [contaJaConsultada, setContaJaConsultada] = useState<string | null>(null);

  useEffect(() => {
    if (modo !== "online" || !sessao.conta) {
      setCarreira(null);
      setContaJaConsultada(null);
      return;
    }
    const idDaConta = sessao.conta.usuario.id;
    // `vivo` evita gravar a resposta de uma conta em cima da tela de outra,
    // que é o que acontece quando alguém sai e entra rápido.
    let vivo = true;
    setCarregandoCarreira(true);
    void carreiraDaConta(sessao.cliente)
      .then((resultado) => {
        if (vivo) setCarreira(resultado);
      })
      .catch(() => {
        if (vivo) setCarreira(null);
      })
      .finally(() => {
        if (!vivo) return;
        setCarregandoCarreira(false);
        setContaJaConsultada(idDaConta);
      });
    return () => {
      vivo = false;
    };
  }, [modo, sessao.conta, sessao.cliente]);
  const [note, setNote] = useState<FraseDaSessao | null>(null);
  const [error, setError] = useState<ErroDaSessao | null>(null);

  const runner = getRunner();

  const refreshSaves = useCallback(async () => {
    try {
      const reply = await runner.send({ type: "listSaves" });
      if (reply.type === "saves") setSaves(reply.saves);
    } catch {
      setSaves([]);
    }
  }, [runner]);

  useEffect(() => {
    void refreshSaves();
  }, [refreshSaves]);

  /** Envolve um comando com estado de ocupado e tratamento de erro. */
  const run = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | null> => {
      setBusy(true);
      try {
        return await action();
      } catch (cause) {
        setError(recusaDoErro(cause));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  /** Comandos que devolvem a visão atualizada e, às vezes, um aviso. */
  const sendAndApply = useCallback(
    async (command: Parameters<typeof runner.send>[0]) => {
      await run(async () => {
        const reply = await runner.send(command);
        if (reply.type === "view") {
          setView(reply.view);
          setError(reply.error);
        }
        return reply;
      });
    },
    [run, runner],
  );

  const createGame = useCallback(async () => {
    setError(null);
    setScreen("gerando");
    const reply = await run(() =>
      runner.send({ type: "create", seed: `lm-${Date.now().toString(36)}` }),
    );
    if (reply?.type === "created") {
      setOptions(reply.options);
      setGenerationMs(reply.generationMs);
      setScreen("escolha");
    } else {
      setScreen("inicio");
    }
  }, [run, runner]);

  /**
   * Abre uma carreira com um banco de dados de arquivo.
   *
   * A leitura acontece aqui, na interface, porque é onde o arquivo chega. A
   * validação acontece no motor, que é quem sabe o que serve, e o erro volta
   * apontando o que está errado e onde.
   */
  const startFromPack = useCallback(
    async (pack: unknown, volta: Screen) => {
      setError(null);
      setScreen("gerando");
      const reply = await run(() =>
        runner.send({
          type: "createFromPack",
          seed: `lm-${Date.now().toString(36)}`,
          pack,
        }),
      );
      if (reply?.type === "created" && reply.options.length > 0) {
        setOptions(reply.options);
        setGenerationMs(reply.generationMs);
        setScreen("escolha");
        return;
      }
      /*
       * O worker devolveu a lista de problemas como chave, valores e caminho,
       * e é aqui que ela vira frase: o caminho não passa pelo dicionário
       * porque é endereço, e sai igual em qualquer idioma.
       */
      if (reply?.type === "created" && reply.problems.length > 0) {
        const detalhe = reply.problems
          .map((problema) => {
            const frase = problemaDeDatapack(problema.key, problema.params);
            return problema.path ? `${problema.path}: ${frase}` : frase;
          })
          .join(" · ");
        setError({ key: "sessao.arquivoNaoServe", params: { problemas: detalhe } });
      }
      setScreen(volta);
    },
    [run, runner],
  );

  const importPack = useCallback(
    async (file: File) => {
      let conteudo: unknown;
      try {
        conteudo = JSON.parse(await file.text());
      } catch {
        setError({ key: "home.erroDeArquivo", params: {} });
        return;
      }
      await startFromPack(conteudo, "inicio");
    },
    [startFromPack],
  );

  /** Gera um banco novo no motor, para o editor ter de onde partir. */
  const makePack = useCallback(
    async (seed: string): Promise<Datapack | null> => {
      const reply = await run(() => runner.send({ type: "makePack", seed }));
      return reply?.type === "packData" ? reply.pack : null;
    },
    [run, runner],
  );

  /*
   * Salvar ou não depois da rodada.
   *
   * No online a escolha não existe: a carreira sobe para o servidor a partir
   * do save, e sem ele a rodada não subiria. No 1 Jogador vale o interruptor
   * da Carreira.
   */
  const salvarNaRodada = (): boolean =>
    modo === "online" || preferenciaLigada("salvamentoAutomatico");

  /** Salva a carreira agora, para quem desligou o salvamento automático. */
  const salvarAgora = useCallback(async () => {
    const reply = await run(() => runner.send({ type: "save" }));
    if (reply?.type === "saved") avisar(t("carreira.salvo"), "recado");
  }, [run, runner, avisar, t]);

  /** Salva o banco de dados da carreira atual num arquivo. */
  const exportPack = useCallback(async () => {
    const reply = await run(() => runner.send({ type: "exportPack" }));
    if (reply?.type !== "pack") return;
    const blob = new Blob([reply.json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = reply.name;
    link.click();
    URL.revokeObjectURL(url);
  }, [run, runner]);

  /*
   * A carreira sobe depois de cada rodada.
   *
   * O motor grava o mundo no IndexedDB ao avançar, como sempre fez; o que mudou
   * é que esse arquivo virou cache. A fonte é o servidor, e é para lá que ele
   * vai assim que a rodada termina.
   *
   * Falha de rede aqui não derruba a partida: a rodada já aconteceu no aparelho
   * e o próximo envio leva tudo junto, porque o mundo é um só e vai inteiro. O
   * que não pode acontecer é o jogo parar de responder porque o servidor
   * demorou.
   */
  const subirCarreira = useCallback(
    async (vista: ClubView) => {
      const conta = sessao.conta;
      if (modo !== "online" || !conta) return;
      try {
        const registro = await readSave(identificadorLocal(conta.usuario.id));
        if (!registro) return;
        const envio = await prepararCarreira(registro, vista.club.id);
        setCarreira(await enviarCarreira(sessao.cliente, envio, carreira?.hash ?? null));
      } catch {
        // Falha de rede não derruba a partida: a rodada já aconteceu no
        // aparelho e o próximo envio leva o mundo inteiro, porque é um só.
        setAvisoDaNuvem(t("carreira.servidor.falhaNoEnvio"));
      }
    },
    [modo, sessao.conta, sessao.cliente, carreira, t],
  );

  /**
   * Cria o clube e assume o comando dele.
   *
   * O caminho é o mesmo de `start`, com um comando diferente: o motor faz o
   * clube nascer na última divisão, refaz o calendário e devolve a visão já com
   * a pessoa no comando. O save sobe na sequência, como em qualquer começo.
   */
  const criarClube = useCallback(
    async (entrada: Omit<EntradaDeClubeCriado, "founder">) => {
      if (!criacao) return;
      setError(null);
      const conta = sessao.conta;
      const saveId =
        modo === "online" && conta ? identificadorLocal(conta.usuario.id) : undefined;

      const reply = await run(() =>
        runner.send({
          type: "criarClube",
          // O técnico da carreira é quem funda: vira história do clube.
          entrada: { ...entrada, founder: criacao.managerName },
          managerName: criacao.managerName,
          saveId,
        }),
      );
      if (reply?.type === "view") {
        setView(reply.view);
        setError(reply.error);
        setScreen("clube");
        setCriacao(null);
        void refreshSaves();
        void subirCarreira(reply.view);
      }
    },
    [criacao, run, runner, refreshSaves, subirCarreira, modo, sessao.conta],
  );

  const start = useCallback(
    async (seats: Array<{ clubId: string; managerName: string }>) => {
      setError(null);
      const conta = sessao.conta;
      /*
       * No online, o identificador do save é o da conta.
       *
       * A sessão inventaria um a cada início, e aí cada temporada nova deixaria
       * uma carreira órfã no aparelho. Com o identificador da conta, o cache
       * local é sempre o mesmo arquivo, sobrescrito.
       */
      const saveId =
        modo === "online" && conta ? identificadorLocal(conta.usuario.id) : undefined;
      const reply = await run(() => runner.send({ type: "pickMany", seats, saveId }));
      if (reply?.type === "view") {
        setView(reply.view);
        setError(reply.error);
        setScreen("clube");
        void refreshSaves();
        void subirCarreira(reply.view);
      }
    },
    /*
     * `modo`, a conta e `subirCarreira` estavam faltando aqui, e o buraco era
     * grande: sem eles esta função guardava para sempre o que valia no
     * primeiro desenho da tela, que é antes de existir sessão.
     *
     * O estrago era duplo e calado. O identificador do save saía indefinido,
     * então o cache local nascia com um nome inventado em vez do nome da
     * conta; e o `subirCarreira` capturado era o de uma sessão vazia, que
     * desiste na primeira linha. Resultado: a carreira de uma conta nova nunca
     * chegava ao servidor, e recarregar a página devolvia a escolha de clube
     * como se nada tivesse sido jogado.
     */
    [run, runner, refreshSaves, subirCarreira, modo, sessao.conta],
  );

  const continueSave = useCallback(
    async (saveId: string) => {
      setError(null);
      const reply = await run(() => runner.send({ type: "load", saveId }));
      if (reply?.type === "view") {
        setView(reply.view);
        setScreen("clube");
        setNote(null);
      }
    },
    [run, runner],
  );

  const removeSave = useCallback(
    async (saveId: string) => {
      const reply = await run(() =>
        runner.send({ type: "deleteSave", saveId }),
      );
      if (reply?.type === "saves") setSaves(reply.saves);
    },
    [run, runner],
  );


  /** Começa a carreira da conta: o mundo nasce aqui e sobe na hora. */
  /**
   * Para onde volta quem fechou loja, ligas, ranqueado, conta ou opções.
   *
   * Estas telas passaram a ser alcançadas de dentro do jogo, pela barra de
   * navegação, então voltar para a abertura seria jogar a pessoa para fora da
   * carreira que ela estava jogando. Com carreira aberta, volta para o hub.
   *
   * A abertura continua sendo o destino de quem não tem carreira aberta: é o
   * caso de quem saiu da conta na tela de conta, e de quem abriu o jogo no
   * aplicativo e ainda não escolheu modo.
   */
  const voltarDeTelaLateral = useCallback(() => {
    setScreen(view ? "clube" : "inicio");
  }, [view]);

  const comecarCarreiraOnline = useCallback(() => {
    setModo("online");
    void createGame();
  }, [createGame]);

  /** Traz o mundo do servidor e abre. O cache local é só o caminho até o motor. */
  const continuarCarreiraOnline = useCallback(async () => {
    const conta = sessao.conta;
    if (!conta) return;
    setError(null);
    const registro = await run(async () => baixarCarreira(sessao.cliente, conta.usuario.id));
    if (!registro) return;
    const reply = await run(() => runner.send({ type: "load", saveId: registro.id }));
    if (reply?.type === "view") {
      setView(reply.view);
      setScreen("clube");
      setNote(null);
    }
  }, [run, runner, sessao.conta, sessao.cliente]);

  /**
   * Entrar na conta já abre o jogo.
   *
   * Depois de entrar, a tela de abertura não tem pergunta a fazer: a conta tem
   * uma carreira e ela abre, ou não tem e a próxima coisa é escolher o clube.
   * Mostrar um painel com um botão no meio do caminho é um clique a mais para
   * dizer o que já estava decidido.
   *
   * `jaRoteou` guarda de qual conta a decisão já foi tomada, e sem ele isto
   * seria uma armadilha: quem apertasse "Voltar" na escolha de clube cairia na
   * abertura e seria mandado de volta para a escolha no mesmo instante, sem
   * jeito de sair. Com ele, voltar leva à abertura e ela fica.
   *
   * Sair da conta limpa a marca, senão entrar de novo não abriria nada.
   */
  const jaRoteou = useRef<string | null>(null);

  useEffect(() => {
    if (!sessao.conta) jaRoteou.current = null;
  }, [sessao.conta]);

  /*
   * Sair da conta devolve à abertura, de onde quer que a pessoa esteja.
   *
   * O botão de sair fica na ficha da conta, e a ficha abre em dois lugares: a
   * tela solta e a seção dentro do hub. Nos dois, sair deixava a pessoa numa
   * tela que passou a não ter o que mostrar, porque o formulário de entrar
   * saiu de lá e foi para a abertura.
   *
   * É a transição que manda, e não o estado: quem joga sozinho é visitante o
   * tempo todo e nunca entrou, então reagir a "sem conta" arrancaria essa
   * pessoa da carreira dela a cada renderização.
   */
  const tinhaConta = useRef(false);
  useEffect(() => {
    const tem = Boolean(sessao.conta);
    if (tinhaConta.current && !tem) setScreen("inicio");
    tinhaConta.current = tem;
  }, [sessao.conta]);

  useEffect(() => {
    const conta = sessao.conta;
    if (modo !== "online" || !conta) return;
    // A resposta ainda está vindo: decidir agora mandaria toda conta com
    // carreira para a escolha de clube, porque `carreira` ainda é `null`.
    if (busy) return;
    // A resposta desta conta precisa ter chegado. Sem isto, o efeito decide no
    // instante em que a sessão aparece, quando `carreira` ainda é `null` por
    // não ter sido perguntada, e toda conta com carreira cairia na escolha de
    // clube como se fosse nova.
    if (carregandoCarreira || contaJaConsultada !== conta.usuario.id) return;
    /*
     * Só nas duas telas de entrada, e só sem carreira aberta.
     *
     * "conta" entra na lista porque dá para entrar por lá também: a abertura
     * tem o formulário, mas quem abriu a ficha da conta deslogado vê o mesmo
     * formulário ali, e ficar parado nela depois de entrar é a mesma parada que
     * este efeito veio tirar do caminho.
     *
     * `view` é o que separa os dois sentidos da mesma tela. Com carreira
     * aberta, "conta" foi aberta de dentro do jogo, pela barra de navegação, e
     * mandar a pessoa de volta ao clube seria arrancá-la do que ela foi fazer.
     */
    if (screen !== "inicio" && screen !== "conta") return;
    if (view) return;
    if (jaRoteou.current === conta.usuario.id) return;

    jaRoteou.current = conta.usuario.id;
    if (carreira) void continuarCarreiraOnline();
    else comecarCarreiraOnline();
  }, [
    modo,
    sessao.conta,
    carreira,
    carregandoCarreira,
    contaJaConsultada,
    screen,
    busy,
    view,
    continuarCarreiraOnline,
    comecarCarreiraOnline,
  ]);

  /**
   * Leva uma cópia para o modo 1 Jogador.
   *
   * O arquivo é entregue ao gerenciador de download do sistema, e o servidor
   * anota a saída. Não existe volta: o que for jogado nessa cópia fica nela.
   */
  const exportarCarreiraOnline = useCallback(async () => {
    await run(async () => {
      const entrega = await exportarCarreira(sessao.cliente);
      const link = document.createElement("a");
      link.href = entrega.url;
      link.download = "carreira.lmsave";
      link.click();
      setCarreira(await carreiraDaConta(sessao.cliente));
      return entrega;
    });
  }, [run, sessao.cliente]);

  /** Apaga a carreira da conta. É o único jeito de recomeçar sem trocar de conta. */
  const apagarCarreiraOnline = useCallback(async () => {
    const conta = sessao.conta;
    if (!conta || !window.confirm(t("home.online.apagarConfirma"))) return;
    await run(async () => {
      await apagarCarreira(sessao.cliente);
      await runner.send({ type: "deleteSave", saveId: identificadorLocal(conta.usuario.id) });
      setCarreira(null);
      setView(null);
      setScreen("inicio");
    });
  }, [run, runner, sessao.conta, sessao.cliente, t]);

  // Contexto do relatório de erros. Sobe semente, temporada e rodada a cada
  // mudança de estado, que é o que permite reproduzir um defeito do motor.
  useEffect(() => {
    if (!view) return;
    contextoDaPartida({
      seed: view.debug.seed,
      season: view.season,
      round: view.round,
      engineVersion: view.debug.engineVersion,
      schemaVersion: view.debug.schemaVersion,
      modo: view.seats.length > 1 ? "hotseat" : "solo",
    });
  }, [view]);

  const advance = useCallback(async () => {
    setError(null);
    const reply = await run(() => runner.send({ type: "advance", salvar: salvarNaRodada() }));
    if (reply?.type === "advanced") {
      setView(reply.view);
      setNote(reply.note);
      if (reply.match) setMatch(reply.match);
      void refreshSaves();
      void subirCarreira(reply.view);
    }
  }, [run, runner, refreshSaves, subirCarreira]);

  /**
   * Assistir à rodada.
   *
   * Abre a rodada parando na partida do clube. Quando o time não joga, a rodada
   * já foi processada pelo motor e só resta atualizar a tela, por isso o
   * caminho de folga cai no mesmo tratamento do avanço normal.
   */
  const watch = useCallback(async () => {
    setError(null);
    const reply = await run(() => runner.send({ type: "liveStart" }));
    if (reply?.type !== "live") return;
    if (reply.live) {
      setLive(reply.live);
      return;
    }
    const fim = await run(() => runner.send({ type: "liveFinish", salvar: salvarNaRodada() }));
    if (fim?.type === "advanced") {
      setView(fim.view);
      setNote(fim.note);
      if (fim.match) setMatch(fim.match);
      void refreshSaves();
      void subirCarreira(fim.view);
    }
  }, [run, runner, refreshSaves, subirCarreira]);

  const liveStep = useCallback(
    async (untilSegment: number) => {
      const reply = await run(() =>
        runner.send({ type: "liveStep", untilSegment }),
      );
      if (reply?.type === "live" && reply.live) setLive(reply.live);
    },
    [run, runner],
  );

  const liveSub = useCallback(
    async (outId: string, inId: string) => {
      const reply = await run(() =>
        runner.send({ type: "liveSub", outId, inId }),
      );
      if (reply?.type === "live") {
        if (reply.live) setLive(reply.live);
        if (reply.error) setError(reply.error);
      }
    },
    [run, runner],
  );

  const liveFinish = useCallback(async () => {
    const reply = await run(() => runner.send({ type: "liveFinish", salvar: salvarNaRodada() }));
    if (reply?.type === "advanced") {
      setLive(null);
      setView(reply.view);
      setNote(reply.note);
      if (reply.match) setMatch(reply.match);
      void refreshSaves();
      void subirCarreira(reply.view);
    }
  }, [run, runner, refreshSaves, subirCarreira]);

  const search = useCallback(
    async (filters: MarketFilters, pagina: number, ordem: OrdemDoMercado | null) => {
      const reply = await run(() => runner.send({ type: "search", filters, pagina, ordem }));
      if (reply?.type === "search")
        setMarket({ results: reply.players, total: reply.total, pagina: reply.pagina, paginas: reply.paginas });
    },
    [run, runner],
  );

  const openPlayer = useCallback(
    async (playerId: string) => {
      const reply = await run(() => runner.send({ type: "detail", playerId }));
      if (reply?.type === "detail") setSheet(reply.player);
    },
    [run, runner],
  );


  /*
   * A ficha montada, para quem for desenhá-la decidir onde.
   *
   * No elenco do computador ela fica na coluna da direita; em todo o resto abre
   * por cima. Quem sabe qual dos dois é a tela, e não esta função: aqui só se
   * junta o jogador aberto com o que se pode fazer com ele.
   */
  const ficha: DadosDaFicha | null =
    sheet && view
      ? {
          player: sheet,
          cash: view.club.cash,
          marketOpen: view.marketOpen,
          busy,
          uniforme: view.club.kit ?? uniformeDasCores(view.club.colors),
          onOffer: (amount, salary, years, installments) => {
            void sendAndApply({
              type: "offer",
              playerId: sheet.id,
              amount,
              salary,
              years,
              installments,
            });
            setSheet(null);
          },
          onBuy:
            modo === "online"
              ? null
              : (years) => {
                  void sendAndApply({ type: "buyNow", playerId: sheet.id, years, salvar: salvarNaRodada() });
                  setSheet(null);
                },
          onScout: () => {
            void sendAndApply({ type: "scout", playerId: sheet.id });
            setSheet(null);
          },
          onLoan: (salaryShare) => {
            void sendAndApply({ type: "loanIn", playerId: sheet.id, salaryShare });
            setSheet(null);
          },
          onAction: (action) => {
            void sendAndApply({ type: "playerAction", playerId: sheet.id, action });
            setSheet(null);
          },
          onClose: () => setSheet(null),
        }
      : null;

  const actions: HubActions = {
    onWatch: () => void watch(),
    onExportPack: () => void exportPack(),
    onResponderColetiva: (coletivaId, resposta) => void sendAndApply({ type: "responderColetiva", coletivaId, resposta }),
    onLerNotificacoes: () => {
      void runner.send({ type: "lerNotificacoes" }).then((reply) => {
        if (reply.type === "view") setView(reply.view);
      });
    },
    onSaveNow: () => void salvarAgora(),
    onSwitchSeat: (index: number) =>
      void sendAndApply({ type: "switchSeat", index }),
    onConfirmLineup: (lineup: LineupInput, tactic: TacticInput) =>
      void sendAndApply({ type: "setLineup", lineup, tactic }),
    onAutoLineup: (tactic) => void sendAndApply({ type: "autoLineup", tactic }),
    onSearch: (filters, pagina, ordem) => void search(filters, pagina, ordem),
    onOpenPlayer: (playerId) => void openPlayer(playerId),
    onRespondOffer: (offerId, action) =>
      void sendAndApply({ type: "respondOffer", offerId, action }),
    onYouthAction: (prospectId, action) =>
      void sendAndApply({ type: "youthAction", prospectId, action }),
    onCancelDecision: (index) =>
      void sendAndApply({ type: "cancelDecision", index }),
    onClubAction: (input) =>
      void sendAndApply({ type: "clubAction", ...input }),
    onExit: () => {
      setScreen("inicio");
      setView(null);
      void refreshSaves();
    },
  };

  return (
    <div
      className="app"
        style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}
      >
        {error && (
          <div
            role="alert"
            onClick={() => setError(null)}
            style={{
              background: "var(--danger)",
              color: "#fff",
              padding: "10px var(--gutter)",
              textAlign: "center",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {textoDoErro(error)}
          </div>
        )}

        {screen === "inicio" && (
          <Home
            modo={modo}
            modos={modosDaPlataforma()}
            aoEscolherModo={setModo}
            busy={busy}
            online={{
              carreira,
              carregando: carregandoCarreira,
              aoComecar: comecarCarreiraOnline,
              aoContinuar: () => void continuarCarreiraOnline(),
              aoExportar: () => void exportarCarreiraOnline(),
              aoApagar: () => void apagarCarreiraOnline(),
              podeExportar: ehAplicativo(),
            }}
            umJogador={{
              saves,
              onNew: () => void createGame(),
              onImportPack: (file: File) => void importPack(file),
              onEditor: () => setScreen("editor"),
              onContinue: (saveId: string) => void continueSave(saveId),
              onDelete: (saveId: string) => void removeSave(saveId),
            }}
          />
        )}

        {/*
          O idioma fica no canto, acima de tudo.

          Ele é a única coisa da tela inicial que vale antes de entrar: quem
          abriu o jogo num idioma que não entende precisa achar isso sem ler
          nada, e um controle no rodapé some abaixo da dobra no celular.

          `env(safe-area-inset-top)` porque no celular o topo da tela é da
          barra de status, e sem a folga o seletor fica embaixo do relógio.
        */}
        {screen === "inicio" && (
          <div
            style={{
              position: "fixed",
              top: "calc(12px + env(safe-area-inset-top, 0px))",
              right: "var(--gutter)",
              zIndex: 5,
            }}
          >
            <SeletorDeIdioma />
          </div>
        )}

        {screen === "inicio" && sessao.conta && (
          <div
            className="row"
            style={{
              gap: 10,
              justifyContent: "center",
              /* A fila passa da largura de um celular: sem quebrar, o último
                 item fica espremido fora da tela. */
              flexWrap: "wrap",
              padding: "0 var(--gutter) 28px",
            }}
          >
            {/*
              Só conta e opções sobram aqui, e por um motivo estreito.
 
              Ligas, ranqueado e loja moram na barra de navegação do jogo, e
              repeti-las aqui era um terceiro caminho para a mesma tela. Estas
              duas ficam porque quem entrou na conta e ainda não tem carreira
              não tem hub nenhum: sem elas, não haveria como sair da conta nem
              trocar de idioma.
            */}
            <button className="ghost" onClick={() => setScreen("conta")}>
              {t("conta.titulo")}
            </button>
            <button className="ghost" onClick={() => setScreen("opcoes")}>
              {t("opcoes.titulo")}
            </button>
          </div>
        )}

        {screen === "conta" && <Conta aoVoltar={voltarDeTelaLateral} />}

        {/*
          Opções também fora da carreira. Idioma, relatório de erros e "sobre"
          valem para quem ainda não começou nenhuma, e recusar o envio de dados
          não pode exigir abrir um clube antes.
        */}
        {screen === "opcoes" && <Opcoes aoVoltar={voltarDeTelaLateral} />}

        {screen === "liga" && ligaOnline && (
          <HubOnline
            leagueId={ligaOnline}
            aoSair={() => {
              setLigaOnline(null);
              // Volta para o jogo, e de lá a barra de navegação leva de volta à
              // lista de ligas, que agora é uma seção e não uma tela.
              voltarDeTelaLateral();
            }}
          />
        )}

        {/*
          O mercado reaproveita a liga que já está aberta, em vez de pedir de
          novo qual é: propor por um jogador sem estar dentro da liga não faz
          sentido nenhum, e um segundo estado para a mesma coisa só criaria
          chance de os dois discordarem.
        */}
        {screen === "mercado" && ligaOnline && (
          <MercadoOnline leagueId={ligaOnline} aoVoltar={() => setScreen("liga")} />
        )}

        {screen === "gerando" && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              textAlign: "center",
              padding: "var(--gutter)",
            }}
          >
            {/* A silhueta correndo vem da arte que o autor desenhou, animada
                quadro a quadro. Ela existe para dar régua ao tempo: sem nada se
                mexendo, dez segundos gerando 8 mil jogadores parecem travamento. */}
            <div className="correndo" role="presentation" />
            <span className="label">{t("novoJogo.gerandoRotulo")}</span>
            <h1 style={{ fontSize: 22 }}>{t("novoJogo.gerandoTitulo")}</h1>
            <p className="muted" style={{ margin: 0 }}>
              {t("novoJogo.gerandoCorpo")}
            </p>
          </div>
        )}

        {screen === "editor" && (
          <Suspense
            fallback={
              <div
                style={{
                  flex: 1,
                  display: "grid",
                  placeItems: "center",
                  padding: 40,
                }}
              >
                <span className="label">{t("editor.titulo")}</span>
              </div>
            }
          >
            <DatabaseEditor
              ocupado={busy}
              inicial={packDoEditor}
              aoSair={() => setScreen("inicio")}
              aoGerar={makePack}
              aoJogar={(pack) => {
                setPackDoEditor(pack);
                void startFromPack(pack, "editor");
              }}
            />
          </Suspense>
        )}

        {screen === "escolha" && (
          <NewGame
            options={options}
            generationMs={generationMs}
            onStart={start}
            aoCriarClube={(countryId, managerName) => {
              setCriacao({ countryId, managerName });
              setScreen("criarClube");
            }}
            onBack={() => setScreen("inicio")}
            permiteHotseat={modo === "umJogador"}
          />
        )}

        {screen === "criarClube" && criacao && (
          <CriarClube
              countryId={criacao.countryId}
              /* Todas as siglas do mundo, para a nova não repetir nenhuma. */
              siglasDoMundo={options.map((option) => option.short)}
              busy={busy}
              aoCriar={(entrada) => void criarClube(entrada)}
            aoVoltar={() => setScreen("escolha")}
          />
        )}

        {screen === "clube" && view && (
          <ClubHub
            ficha={ficha}
            online={
              modo === "online" && sessao.conta
                ? {
                    aoAbrirLiga: (leagueId) => {
                      setLigaOnline(leagueId);
                      setScreen("liga");
                    },
                  }
                : null
            }
            view={view}
            busy={busy}
            note={note}
            market={market}
            actions={actions}
          />
        )}

        {/*
        A tela de fim de temporada cobre tudo enquanto houver balanço para ler.
        Ela some sozinha quando a próxima temporada começa, porque o motor limpa
        o relatório na virada.
      */}
        {screen === "clube" && view?.seasonReport && (
          <SeasonEnd
            report={view.seasonReport}
            clube={view.club}
            busy={busy}
            /* O balanço cobre o hub inteiro enquanto está aberto, então a
               decisão da base precisa caber aqui dentro: senão o treinador lê
               que a base pede decisão e não tem onde decidir. */
            youth={view.youth}
            onYouthAction={(prospectId, action) => {
              void sendAndApply({ type: "youthAction", prospectId, action });
            }}
            onRenew={(playerId) => {
              void sendAndApply({
                type: "playerAction",
                playerId,
                action: "renovar",
              });
            }}
            onTakeJob={(clubId) => {
              void sendAndApply({ type: "takeJob", clubId });
            }}
            onContinue={advance}
          />
        )}

        {live && (
          <LiveMatch
            live={live}
            busy={busy}
            onStep={(untilSegment) => void liveStep(untilSegment)}
            onSub={(outId, inId) => void liveSub(outId, inId)}
            onFinish={() => void liveFinish()}
          />
        )}

        {match && <MatchPanel match={match} onClose={() => setMatch(null)} />}

        {/*
          O token de push acompanhando a sessão. Também não desenha nada, e
          nunca pede permissão: quem pede é o saguão da liga assíncrona, onde o
          motivo existe. Aqui só acontecem as duas pontas que nenhuma tela vê,
          porque elas são fora de tela: reconfirmar o token de quem entrou e
          apagá-lo de quem saiu.
        */}
        <SincroniaDePush />

        {/*
          O idioma escolhido acompanhando a conta. Também não desenha nada, e
          fica aqui, e não junto do seletor, porque a conta pode chegar em
          qualquer tela: quem entra pelo hub de ligas num aparelho novo merece o
          jogo no idioma que já escolheu no outro.
        */}
        <SincroniaDeIdioma />
    </div>
  );
}
